import { useState } from "react";
import { message } from "antd";
import { InvoiceServices } from "../services/invoice";
import { BatchServices } from "../services/batch";
import { useMessageApi } from "../context/MessageContext";

const isPreviewFileType = (fileName: string): boolean => {
  if (!fileName) return false;

  const ext = fileName.split(".").pop()?.toLowerCase();

  if (!ext) return false;

  const previewExtensions = [
    // Document
    "doc", "docx", "odt", "rtf",
    // Spreadsheet
    "xls", "xlsx", "ods", "csv", "tsv",
    // Presentation
    "ppt", "pptx", "odp",
  ];

  return previewExtensions.includes(ext);
};

export const useViewFile = () => {
  const [loading, setLoading] = useState(false);
  const messageApi = useMessageApi();

  const onViewFile = async (
    invoiceId: number,
    invoiceAttachmentId: number,
    fileName: string,
    fileType?: string,
  ) => {
    if ((!fileType || fileType === "file") && isPreviewFileType(fileName)) {
      await onPreviewFile(invoiceId, invoiceAttachmentId);
      return;
    }
    await onDownloadAttachment(invoiceId, invoiceAttachmentId, fileName, fileType);
  }

  const onDownloadAttachment = async (
    invoiceId: number,
    invoiceAttachmentId: number,
    fileName: string,
    fileType?: string,
  ) => {
    try {
      setLoading(true);
      let fileNameDownload = fileName;
      const baseName = fileName.replace(/\.[^/.]+$/, "");
      if (fileType === "data") {
        fileNameDownload = `${baseName}.json`;
      } else if (fileType === "debug") {
        fileNameDownload = `${baseName}_debug.json`;
      }

      // Call API download
      const response: Blob = await InvoiceServices.downloadFile(
        invoiceId,
        invoiceAttachmentId,
        fileType
      );

      const blobUrl = window.URL.createObjectURL(response);
      window.open(blobUrl, "_blank");

      // Trigger download
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = fileNameDownload;
      document.body.appendChild(a);
      a.click();
      a.remove();

      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 10000);
    } catch (error) {
      console.error("Download failed:", error);
      messageApi.error("Download failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const onPreviewFile = async (
    invoiceId: number,
    invoiceAttachmentId: number
  ) => {
    try {
      setLoading(true);
      const response = await InvoiceServices.previewUrl(
        invoiceId,
        invoiceAttachmentId
      );

      const previewUrl = response.data.previewUrl;
      if (!previewUrl) {
        console.error("Preview failed: no preview URL returned");
        messageApi.error("Preview failed. No preview URL available.");
        return;
      }

      const iframeWindow = window.open("", "_blank");
      if (!iframeWindow) {
        messageApi.error("Cannot open preview window. Please allow popups.");
        return;
      }

      iframeWindow.document.body.innerHTML = `
          <iframe
            srcdoc='<html><body style="margin:0;padding:0;"><iframe src="${previewUrl}" style="width:100%;height:100vh;border:none;"></iframe></body></html>'
            style="width:100%;height:100vh;border:none;"
          ></iframe>
        `;
    } catch (error) {
      console.error("Preview failed:", error);
      messageApi.error("Preview failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const onDownloadBatchExport = async (batchId: number, batchName: string) => {
    try {
      setLoading(true);

      const response = await BatchServices.onDownloadBatchExport(batchId);
      if (!response) {
        messageApi.error("Download failed. Please try again.");
        return;
      }
      const { fileName, fileBase64 } = response.data ?? {};
      const fileNameFallback = `Sage-Import-${batchName}_${Date.now()}.csv`;
      const blob = new Blob([Uint8Array.from(atob(fileBase64), c => c.codePointAt(0)!)], {
        type: "text/csv",
      });

      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = fileName || fileNameFallback;
      link.click()

      messageApi.success(`Downloaded ${fileName} successfully.`);
    } catch (error) {
      console.error("Download failed:", error);
      messageApi.error("Download failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    onViewFile,
    onDownloadBatchExport,
  };
};
